'use client';
import { UserRole } from '@repo/shared/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { trpc } from '@/providers/trpc-client';
import type { OrganizationSession } from '@/types/auth';

interface OrganizationState {
  organizations: OrganizationSession[];
  currentOrganization: OrganizationSession;
  isLoading: boolean;
}

interface OrganizationActions {
  setOrganizations: (organizations: OrganizationSession[]) => void;
  setCurrentOrganization: (organization: OrganizationSession) => void;
  updateOrganization: (orgId: string, updates: Partial<OrganizationSession>) => void;
  clearOrganizations: () => void;
  fetchOrganizations: () => Promise<void>;
}

type OrganizationStore = OrganizationState & OrganizationActions;

const initialState: OrganizationState = {
  organizations: [],
  currentOrganization: {
    id: '',
    name: '',
    organizationId: '',
    createdAt: new Date(),
    currentOrganizationRole: UserRole.ORG_VIEWER,
  },
  isLoading: false,
};

export const useOrganizationStore = create<OrganizationStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setOrganizations: (organizations) => {
        set({ organizations });
      },

      setCurrentOrganization: (organization) => {
        const currentOrganization = get().currentOrganization;
        if (currentOrganization?.id === organization?.id) {
          return;
        }
        set({ currentOrganization: organization });
      },

      updateOrganization: (orgId, updates) => {
        set((state) => {
          const updatedOrgs = state.organizations.map((org) =>
            org.id === orgId ? { ...org, ...updates } : org,
          );
          const updatedCurrent =
            state.currentOrganization?.id === orgId
              ? { ...state.currentOrganization, ...updates }
              : state.currentOrganization;
          return {
            organizations: updatedOrgs,
            currentOrganization: updatedCurrent,
          };
        });
      },

      clearOrganizations: () => {
        set(initialState);
      },

      fetchOrganizations: async () => {
        set({ isLoading: true });
        try {
          const organizations = await trpc.organization.getOrganizationSession.query(undefined, {
            context: {
              skipBatch: true,
            },
          });
          const currentOrganization =
            organizations.find((org) => org.id === get().currentOrganization.id) ||
            organizations[0];
          set((state) => {
            return {
              organizations,
              isLoading: false,
              currentOrganization: currentOrganization || state.currentOrganization,
            };
          });
        } catch (error) {
          console.error('Failed to fetch organizations:', error);
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'organization-store',
      partialize: (state) => ({
        organizations: state.organizations,
        currentOrganization: state.currentOrganization,
        isLoading: state.isLoading,
      }),
    },
  ),
);
